import React, { useEffect, useMemo, useRef, useState } from "react";

type Word = { text: string; polish: string; czech?: string };

type Props = {
    words: Word[];
    /** ile słów pokazać w ćwiczeniu (domyślnie 10) */
    limit?: number;
    /** procent liter do ukrycia (0–1), domyślnie 0.4 */
    hideRatio?: number;
    /** kolor brandu (przyciski/akcenty) */
    primaryColor?: string;
};

/** tylko litery angielskie; inne znaki traktujemy jako stałe/separatory */
const isLatinLetter = (ch: string) => /^[A-Za-z]$/.test(ch);

/** wybiera indeksy liter do ukrycia (bez pierwszej i ostatniej) */
function pickHiddenIndexes(letters: string[], ratio: number) {
    const idxs: number[] = [];
    for (let i = 0; i < letters.length; i++) {
        if (isLatinLetter(letters[i])) idxs.push(i);
    }
    if (idxs.length <= 2) return new Set<number>(); // za mało liter
    const eligible = idxs.slice(1, -1); // bez pierwszej i ostatniej litery
    const hideCount = Math.max(1, Math.floor(eligible.length * ratio));
    const toHide = new Set<number>();
    while (toHide.size < Math.min(hideCount, eligible.length)) {
        const j = Math.floor(Math.random() * eligible.length);
        toHide.add(eligible[j]);
    }
    return toHide;
}

type Cell = {
    char: string;        // litera lub znak
    editable: boolean;   // czy użytkownik wpisuje
    prefilled: boolean;  // gotowa litera (zablokowana)
    value: string;       // aktualna wartość inputu (dla editable)
};

type RowState = {
    id: number;
    hint: string;        // polskie tłumaczenie
    solution: string;    // pełne słowo
    cells: Cell[];
};

const HiddenLettersAdults: React.FC<Props> = ({
    words,
    limit = 10,
    hideRatio = 0.4,
    primaryColor = "#F6851F",
}) => {
    // przygotowanie rzędów (jeden rząd = jedno słowo)
    const rows = useMemo<RowState[]>(() => {
        return words.slice(0, limit).map((w, idx) => {
            const letters = w.text.split("");
            const hidden = pickHiddenIndexes(letters, hideRatio);

            const cells: Cell[] = letters.map((ch, i) => {
                if (!isLatinLetter(ch)) {
                    // spacje i znaki specjalne pokazujemy jako stałe „pola” (nieedytowalne)
                    return { char: ch, editable: false, prefilled: true, value: ch };
                }
                if (hidden.has(i)) {
                    return { char: ch.toUpperCase(), editable: true, prefilled: false, value: "" };
                }
                // widoczna (prefill) litera
                return { char: ch.toUpperCase(), editable: false, prefilled: true, value: ch.toUpperCase() };
            });

            return {
                id: idx,
                hint: w.polish,
                solution: w.text.toUpperCase(),
                cells,
            };
        });
    }, [words, limit, hideRatio]);

    // stan wpisanych wartości oraz „sprawdzono/nie”
    const [answers, setAnswers] = useState(() =>
        rows.map(r => r.cells.map(c => (c.editable ? "" : c.value)))
    );
    const [checked, setChecked] = useState<boolean[]>(() => rows.map(() => false));

    // refs do inputów dla auto-focus i nawigacji
    const inputRefs = useRef<HTMLInputElement[][]>([]);

    useEffect(() => {
        inputRefs.current = rows.map(r => r.cells.map(() => null as any));
    }, [rows.length]);

    const setRef =
        (rowIdx: number, cellIdx: number) =>
            (el: HTMLInputElement | null) => {
                if (!inputRefs.current[rowIdx]) inputRefs.current[rowIdx] = [];
                inputRefs.current[rowIdx][cellIdx] = el as any;
            };

    // pomocnicy do przeskoków między polami
    const focusNext = (rowIdx: number, cellIdx: number) => {
        const row = rows[rowIdx];
        for (let i = cellIdx + 1; i < row.cells.length; i++) {
            if (row.cells[i].editable) {
                inputRefs.current[rowIdx][i]?.focus();
                return;
            }
        }
    };
    const focusPrev = (rowIdx: number, cellIdx: number) => {
        const row = rows[rowIdx];
        for (let i = cellIdx - 1; i >= 0; i--) {
            if (row.cells[i].editable) {
                inputRefs.current[rowIdx][i]?.focus();
                return;
            }
        }
    };

    const onInputChange = (rowIdx: number, cellIdx: number, raw: string) => {
        const v = raw.slice(-1).toUpperCase(); // pojedynczy znak
        setAnswers(prev => {
            const next = prev.map(a => [...a]);
            next[rowIdx][cellIdx] = v;
            return next;
        });
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, rowIdx: number, cellIdx: number) => {
        const key = e.key.toLowerCase();
        if (key === "backspace") {
            if (!answers[rowIdx][cellIdx]) {
                // puste -> cofamy focus do poprzedniego
                e.preventDefault();
                focusPrev(rowIdx, cellIdx);
            } else {
                // skasuj aktualną
                setAnswers(prev => {
                    const next = prev.map(a => [...a]);
                    next[rowIdx][cellIdx] = "";
                    return next;
                });
            }
        } else if (key === "arrowright") {
            e.preventDefault();
            focusNext(rowIdx, cellIdx);
        } else if (key === "arrowleft") {
            e.preventDefault();
            focusPrev(rowIdx, cellIdx);
        }
    };

    const onCheckRow = (rowIdx: number) => {
        setChecked(prev => {
            const n = [...prev];
            n[rowIdx] = true;
            return n;
        });
    };

    const resetRow = (rowIdx: number) => {
        setAnswers(prev => {
            const next = prev.map((row, i) =>
                i === rowIdx ? rows[rowIdx].cells.map(c => (c.editable ? "" : c.value)) : row
            );
            return next;
        });
        setChecked(prev => {
            const n = [...prev];
            n[rowIdx] = false;
            return n;
        });
        // focus na pierwsze edytowalne pole
        const firstEditable = rows[rowIdx].cells.findIndex(c => c.editable);
        if (firstEditable >= 0) inputRefs.current[rowIdx][firstEditable]?.focus();
    };

    const resetAll = () => {
        setAnswers(rows.map(r => r.cells.map(c => (c.editable ? "" : c.value))));
        setChecked(rows.map(() => false));
    };

    const rowIsCorrect = (rowIdx: number) => {
        const sol = rows[rowIdx].solution;
        const built = rows[rowIdx].cells
            .map((c, i) => (c.editable ? (answers[rowIdx][i] || "") : c.value))
            .join("");
        return built.toUpperCase() === sol.toUpperCase();
    };

    const allCorrect = checked.every((c, i) => c && rowIsCorrect(i));

    return (
        <div className="bg-white shadow-md rounded-xl p-4">
            <h4 className="font-bold mb-3">Ćwiczenie: Ukryte litery</h4>

            <div className="space-y-4">
                {rows.map((row, rowIdx) => {
                    const isChecked = checked[rowIdx];
                    const correct = isChecked && rowIsCorrect(rowIdx);

                    return (
                        <div key={row.id} className="p-3 rounded-lg border bg-white">
                            <div className="text-sm mb-2 opacity-70">Tłumaczenie: {row.hint}</div>

                            {/* rząd krat */}
                            <div className="flex flex-wrap gap-2">
                                {row.cells.map((cell, cellIdx) => {
                                    if (!isLatinLetter(cell.char)) {
                                        // Dla spacji wstawiamy „przerwę” o szerokości kratki;
                                        // dla znaków typu '-' pokazujemy małe, wyszarzone pudełko.
                                        if (cell.char === " ") {
                                            return <div key={cellIdx} className="w-8" />;
                                        }
                                        return (
                                            <div
                                                key={cellIdx}
                                                className="w-8 h-10 rounded-md border flex items-center justify-center text-sm opacity-50 bg-zinc-50"
                                            >
                                                {cell.char}
                                            </div>
                                        );
                                    }

                                    const target = cell.char.toUpperCase();
                                    const current = answers[rowIdx][cellIdx]?.toUpperCase() || "";
                                    const stateClass =
                                        isChecked && cell.editable
                                            ? current === target
                                                ? "border-green-400 bg-green-50"
                                                : "border-red-400 bg-red-50"
                                            : "border-zinc-300 bg-white";

                                    if (!cell.editable) {
                                        // prefilled (zablokowane)
                                        return (
                                            <div
                                                key={cellIdx}
                                                className="w-8 h-10 rounded-md border bg-zinc-100 text-zinc-700 font-semibold flex items-center justify-center select-none"
                                            >
                                                {cell.value}
                                            </div>
                                        );
                                    }

                                    return (
                                        <input
                                            key={cellIdx}
                                            ref={setRef(rowIdx, cellIdx)}
                                            maxLength={1}
                                            value={answers[rowIdx][cellIdx] || ""}
                                            onChange={(e) => {
                                                const v = e.target.value.replace(/[^A-Za-z]/g, "");
                                                onInputChange(rowIdx, cellIdx, v);
                                                if (v) focusNext(rowIdx, cellIdx);
                                            }}
                                            onKeyDown={(e) => onKeyDown(e, rowIdx, cellIdx)}
                                            className={`w-8 h-10 text-center rounded-md border font-semibold uppercase outline-none ${stateClass}`}
                                        />
                                    );
                                })}
                            </div>

                            {/* akcje i feedback */}
                            <div className="mt-3 flex flex-wrap gap-2 items-center">
                                <button
                                    onClick={() => onCheckRow(rowIdx)}
                                    className="px-3 py-2 rounded-md text-white font-semibold"
                                    style={{ backgroundColor: primaryColor }}
                                >
                                    Sprawdź
                                </button>
                                <button
                                    onClick={() => resetRow(rowIdx)}
                                    className="px-3 py-2 rounded-md border font-semibold"
                                    style={{ borderColor: primaryColor, color: primaryColor }}
                                >
                                    Wyczyść
                                </button>

                                {isChecked && correct && (
                                    <span className="text-green-700 font-semibold">✓ Dobrze!</span>
                                )}
                                {isChecked && !correct && (
                                    <span className="text-red-700 font-semibold">✗ Spróbuj ponownie</span>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="mt-4 flex gap-2 items-center">
                <button
                    onClick={resetAll}
                    className="px-3 py-2 rounded-md text-white font-semibold"
                    style={{ backgroundColor: primaryColor }}
                >
                    Wyczyść wszystkie
                </button>
                {allCorrect && (
                    <div className="px-3 py-2 rounded-md bg-green-600 text-white font-semibold">
                        Perfect, congratulations! 🎉
                    </div>
                )}
            </div>
        </div>
    );
};

export default HiddenLettersAdults;
